import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Shield,
  XCircle,
  RefreshCw,
  Loader,
  AlertCircle,
  ArrowLeft
} from "lucide-react";
import axios from "../../config/axios";
import toast from "react-hot-toast";

// Import new sub-components
import { ConnectionHeader } from "./components/ConnectionHeader";
import { SetupProgress } from "./components/SetupProgress";
import { StatusTab } from "./tabs/StatusTab";
import { TimelineTab } from "./tabs/TimelineTab";
import { SchemaTab } from "./tabs/SchemaTab";
import { SettingsTab } from "./tabs/SettingsTab";

/**
 * ConnectionManager Component
 * Airbyte-style layout to manage connector connections
 * Tabs: Status, Timeline, Schema, Mappings, Transformation, Settings
 */
const ConnectionManager = () => {
  const { instanceId } = useParams();
  const navigate = useNavigate();

  // Active tab
  const [activeTab, setActiveTab] = useState("status");

  // Data states
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [connector, setConnector] = useState(null);
  const [dataSource, setDataSource] = useState(null);
  const [streams, setStreams] = useState([]);
  const [allStreams, setAllStreams] = useState(null); // Full streams list for Schema tab
  const [jobs, setJobs] = useState([]);

  // UI states
  const [connectionName, setConnectionName] = useState("");
  const [syncSchedule, setSyncSchedule] = useState("manual");
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pollingWorkflow, setPollingWorkflow] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [customCron, setCustomCron] = useState("0 0 12 * * ?");


  // Fetch connection details
  const fetchConnectionDetails = useCallback(async () => {
    try {
      setLoading(true);

      const instanceResponse = await axios.get(
        `/api/connectors/instances/${instanceId}`,
      );

      setDataSource(instanceResponse.data);
      const connectorId = instanceResponse.data.connectorId;

      const connectorResponse = await axios.get(
        `/api/connectors/${connectorId}`,
      );
      setConnector(connectorResponse.data.connector);

      // Set initial name from instance
      setConnectionName(
        instanceResponse.data.name ||
        `${connectorResponse.data.connector.name} Connection`,
      );

      // Check if connection is still being set up
      if (instanceResponse.data.connectionStatus === "setting_up") {
        setPollingWorkflow(true);
        setLoading(false);
        pollWorkflowStatus();
      } else if (instanceResponse.data.airbyteConnectionId) {
        setLoading(false);
        setTabLoading(true);
        await fetchConnectionConfig(instanceResponse.data.airbyteConnectionId);
        setTabLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Failed to fetch connection details:", error);
      toast.error("Failed to load connection details");
      navigate("/connectors");
      setLoading(false);
    }
  }, [instanceId, navigate]);

  // Poll workflow status when setting up
  const pollWorkflowStatus = useCallback(async () => {
    try {
      const response = await axios.get(
        `/api/connectors/instances/${instanceId}/workflow-status`,
      );

      if (response.data.status === "COMPLETED") {
        setPollingWorkflow(false);
        toast.success("Connection setup complete!");
        fetchConnectionDetails();
      } else if (response.data.status === "FAILED") {
        setPollingWorkflow(false);
        toast.error(
          `Connection setup failed: ${response.data.connectionError || "Unknown error"}`,
        );
        fetchConnectionDetails();
      } else if (response.data.status === "RUNNING") {
        setTimeout(pollWorkflowStatus, 3000);
      }
    } catch (error) {
      console.error("Failed to poll workflow status:", error);
      setPollingWorkflow(false);
    }
  }, [instanceId, fetchConnectionDetails]);

  // Fetch connection configuration from Airbyte
  const fetchConnectionConfig = async (connectionId) => {
    try {
      const airbyteConnectionId =
        connectionId || dataSource?.airbyteConnectionId;

      if (!airbyteConnectionId) {
        console.log("No Airbyte connection ID available");
        return;
      }

      const response = await axios.get(
        `/api/connectors/connections/${airbyteConnectionId}/configuration`,
      );

      const config = response.data;

      // Update connection name from Airbyte if available
      if (config.name) {
        setConnectionName(config.name);
      }

      // Map streams with enabled status based on selected field
      const mappedStreams = (config.streams || []).map((stream) => ({
        name: stream.name,
        namespace: stream.namespace,
        enabled: stream.selected !== false,
        syncMode: stream.syncMode || "full_refresh",
        destinationSyncMode: stream.destinationSyncMode || "overwrite",
        supportedSyncModes: [
          "full_refresh_overwrite",
          "full_refresh_append",
          "incremental_append",
          "incremental_deduped_history",
        ],
      }));

      setStreams(mappedStreams);

      // Parse schedule
      const schedule = config.schedule || "manual";
      if (schedule === "cron") {
        const cronExpr = config.customCron || "";
        if (cronExpr === "0 0 12 * * ?") { // Daily at 12 PM (noon)
          // Keep as custom potentially or map to daily if it matches exact common pattern
          // For now, let's treat specific ones as presets if we want, or just custom
          if (cronExpr === "0 2 * * *") {
            setSyncSchedule("daily");
          } else if (cronExpr === "0 * * * *") {
            setSyncSchedule("hourly");
          } else {
            setSyncSchedule("custom");
            setCustomCron(cronExpr);
          }
        } else if (cronExpr === "0 2 * * *") {
          setSyncSchedule("daily");
        } else if (cronExpr === "0 * * * *") {
          setSyncSchedule("hourly");
        } else {
          setSyncSchedule("custom");
          setCustomCron(cronExpr);
        }
      } else {
        setSyncSchedule(schedule);
      }

      await fetchJobs();
    } catch (error) {
      console.error("Failed to fetch connection configuration:", error);
    }
  };

  // Fetch sync jobs for timeline
  const fetchJobs = async () => {
    try {
      if (!dataSource?.airbyteConnectionId) return;

      const response = await axios.get(
        `/api/connectors/connections/${dataSource.airbyteConnectionId}/status`,
      );

      // Map jobs to consistent format
      const mappedJobs = (response.data.recentJobs || []).map((job) => ({
        id: job.id,
        status: job.status,
        jobType: "Sync",
        startTime: job.startedAt || job.createdAt,
        completedAt: job.completedAt,
        rowsSynced: job.recordsProcessed,
        duration: job.duration,
        error: job.error,
      }));

      setJobs(mappedJobs);
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
    }
  };

  // Fetch all available streams for Schema tab
  const fetchAllStreams = useCallback(async (forceRefresh = false) => {
    try {
      if (!dataSource?.airbyteConnectionId) return;

      setSchemaLoading(true);
      if (forceRefresh) {
        toast.loading("Refreshing schema from source...", { id: "refresh-schema" });
      }

      const response = await axios.get(
        `/api/connectors/connections/${dataSource.airbyteConnectionId}/streams${forceRefresh ? "?refresh=true" : ""}`,
      );

      if (forceRefresh) {
        toast.dismiss("refresh-schema");
        toast.success("Schema refreshed!");
      }

      const mappedStreams = (response.data.streams || []).map((stream) => ({
        name: stream.name,
        namespace: stream.namespace,
        enabled: stream.selected !== false,
        syncMode: stream.syncMode || "full_refresh",
        destinationSyncMode: stream.destinationSyncMode || "overwrite",
        supportedSyncModes: stream.supportedSyncModes || ["full_refresh"],
      }));

      setAllStreams(mappedStreams);
    } catch (error) {
      console.error("Failed to fetch all streams:", error);
      toast.error("Failed to load streams");
    } finally {
      setSchemaLoading(false);
    }
  }, [dataSource?.airbyteConnectionId]);

  // Trigger manual sync
  const triggerSync = async () => {
    try {
      setSyncing(true);

      await axios.post(
        `/api/connectors/connections/${dataSource.airbyteConnectionId}/sync`,
      );

      toast.success("Sync started!");
      setTimeout(fetchJobs, 2000);
    } catch (error) {
      console.error("Failed to trigger sync:", error);
      toast.error("Failed to start sync");
    } finally {
      setSyncing(false);
    }
  };

  // Save stream configuration (Status Tab updates logic also depends on this)
  const saveSchemaStreamConfig = async () => {
    try {
      setSaving(true);

      if (!dataSource?.airbyteConnectionId) {
        toast.error("No connection configured");
        return;
      }

      const streamsToSave = allStreams || streams;
      const streamConfigs = streamsToSave.map((stream) => ({
        name: stream.name,
        selected: stream.enabled,
        syncMode: stream.syncMode || "full_refresh",
        destinationSyncMode: stream.destinationSyncMode || "overwrite",
      }));

      await axios.patch(
        `/api/connectors/connections/${dataSource.airbyteConnectionId}`,
        { streams: streamConfigs },
      );

      // Update the configured streams (status tab) to reflect changes
      setStreams(streamsToSave.filter((s) => s.enabled));

      toast.success("Stream configuration saved!");
    } catch (error) {
      console.error("Failed to save streams:", error);
      toast.error("Failed to save stream configuration");
    } finally {
      setSaving(false);
    }
  };

  // Save settings
  const saveSettings = async () => {
    try {
      setSaving(true);

      if (!dataSource?.airbyteConnectionId) {
        toast.error("No connection configured");
        return;
      }

      await axios.patch(
        `/api/connectors/connections/${dataSource.airbyteConnectionId}`,
        {
          schedule: syncSchedule,
          customCron: syncSchedule === 'custom' ? customCron : undefined,
        },
      );

      toast.success("Settings saved!");
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Delete connection
  const deleteConnection = async () => {
    try {
      await axios.delete(`/api/connectors/instances/${instanceId}`);
      toast.success("Connection deleted");
      navigate("/connectors");
    } catch (error) {
      console.error("Failed to delete connection:", error);
      toast.error("Failed to delete connection");
    }
  };

  // Handle stream updates from SchemaTab
  const handleUpdateStream = (updatedStreams) => {
    if (allStreams) {
      setAllStreams(updatedStreams);
    } else {
      setStreams(updatedStreams);
    }
  };

  // Initiate OAuth flow
  const initiateOAuth = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `/api/connectors/instances/${instanceId}/oauth/initiate`,
      );

      // Redirect to OAuth consent URL
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error("Failed to initiate OAuth:", error);
      toast.error(
        error.response?.data?.error?.message || "Failed to start authorization",
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConnectionDetails();
  }, [fetchConnectionDetails]);

  // Render Logic

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  // Pending authorization state - show OAuth button
  if (
    !dataSource?.airbyteSourceId &&
    (dataSource?.connectionStatus === "pending" ||
      dataSource?.oauthState === "pending")
  ) {
    return (
      <div className="max-w-4xl mx-auto py-16 px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-primary-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Authorization Required
          </h2>
          <p className="text-gray-600 mb-2">
            Connect your {connector?.name || "data source"} account to start
            syncing data.
          </p>
          <p className="text-sm text-gray-500 mb-8">
            You'll be redirected to authorize access securely.
          </p>
          <button
            onClick={initiateOAuth}
            className="px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium inline-flex items-center"
          >
            <Shield className="w-5 h-5 mr-2" />
            Authorize {connector?.name || "Connection"}
          </button>
        </div>
      </div>
    );
  }

  // Failed authorization state
  if (dataSource?.oauthState === "failed" && !dataSource?.airbyteSourceId) {
    return (
      <div className="max-w-4xl mx-auto py-16 px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Authorization Failed
          </h2>
          <p className="text-gray-600 mb-2">
            There was a problem authorizing your{" "}
            {connector?.name || "data source"} account.
          </p>
          {dataSource?.oauthError && (
            <p className="text-sm text-red-600 mb-8">{dataSource.oauthError}</p>
          )}
          <button
            onClick={initiateOAuth}
            className="px-8 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium inline-flex items-center"
          >
            <RefreshCw className="w-5 h-5 mr-2" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Authorized but connection setup in progress (intermediate state before setting_up)
  if (
    dataSource?.airbyteSourceId &&
    !dataSource?.airbyteConnectionId &&
    (dataSource?.connectionStatus === "authorized" ||
      dataSource?.connectionStatus === "creating_connection")
  ) {
    return (
      <div className="max-w-4xl mx-auto py-16 px-4">
        <div className="text-center">
          <Loader className="w-16 h-16 text-blue-600 animate-spin mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Authorization Successful
          </h2>
          <p className="text-gray-600 mb-6">
            Your account has been authorized. The connection is being created...
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-center text-sm text-blue-700">
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              <span>Initializing data pipeline...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Setup Progress views (Setting Up, Failed)
  if (dataSource?.connectionStatus === "setting_up" || pollingWorkflow || retrying || dataSource?.connectionStatus === "failed" ||
    (!dataSource?.airbyteConnectionId && dataSource?.connectionError)) {
    return (
      <SetupProgress
        dataSource={dataSource}
        pollingWorkflow={pollingWorkflow}
        retrying={retrying}
        instanceId={instanceId}
        setRetrying={setRetrying}
        setPollingWorkflow={setPollingWorkflow}
        pollWorkflowStatus={pollWorkflowStatus}
        connectorName={connector?.name}
      />
    );
  }

  // No connection exists - show error state
  if (!dataSource?.airbyteConnectionId) {
    return (
      <div className="max-w-4xl mx-auto py-16 px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-yellow-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Connection Not Found
          </h2>
          <p className="text-gray-600 mb-8">
            This connection hasn't been fully set up yet. Please try refreshing
            the page or contact support if the issue persists.
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium inline-flex items-center"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Refresh Page
            </button>
            <button
              onClick={() => navigate("/connectors")}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium inline-flex items-center"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Connectors
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ConnectionHeader
        connector={connector}
        connectionName={connectionName}
        syncSchedule={syncSchedule}
        dataSource={dataSource}
        syncing={syncing}
        triggerSync={triggerSync}
      />

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex space-x-8">
            {[
              { id: "status", label: "Status" },
              { id: "timeline", label: "Timeline" },
              { id: "schema", label: "Schema" },
              { id: "settings", label: "Settings" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  // Fetch all streams when Schema tab is clicked
                  if (tab.id === "schema" && !allStreams && !schemaLoading) {
                    fetchAllStreams();
                  }
                }}
                className={`py-4 border-b-2 font-medium text-sm ${activeTab === tab.id
                  ? "border-primary-600 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto">
        {activeTab === "status" && (
          <StatusTab
            jobs={jobs}
            streams={streams}
            loading={tabLoading}
            syncing={syncing}
          />
        )}
        {activeTab === "timeline" && <TimelineTab jobs={jobs} />}
        {activeTab === "schema" && (
          <SchemaTab
            allStreams={allStreams}
            streams={streams}
            schemaLoading={schemaLoading}
            fetchAllStreams={fetchAllStreams}
            saveSchemaStreamConfig={saveSchemaStreamConfig}
            saving={saving}
            onUpdateStream={handleUpdateStream}
          />
        )}
        {activeTab === "settings" && (
          <SettingsTab
            connectionName={connectionName}
            setConnectionName={setConnectionName}
            syncSchedule={syncSchedule}
            setSyncSchedule={setSyncSchedule}
            customCron={customCron}
            setCustomCron={setCustomCron}
            saveSettings={saveSettings}
            saving={saving}
            deleteConnection={deleteConnection}
          />
        )}
      </div>
    </div>
  );
};

export default ConnectionManager;
